import { Download, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import html2pdf from 'html2pdf.js';

export default function BrandKit() {
  const handleDownload = () => {
    const element = document.getElementById('brand-kit-content');
    const opt = {
      margin: 0.5,
      filename: 'Mystic-Forest-Brand-Kit.pdf',
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' as const }
    };
    html2pdf().set(opt).from(element).save();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Action Buttons - Hidden when printing */}
      <div className="fixed top-4 right-4 z-50 print:hidden flex gap-2">
        <Button onClick={handleDownload} className="gap-2">
          <Download className="w-4 h-4" />
          Download PDF
        </Button>
        <Button onClick={handlePrint} variant="outline" className="gap-2">
          <Printer className="w-4 h-4" />
          Print
        </Button>
      </div>

      {/* Main Content */}
      <div id="brand-kit-content" className="max-w-5xl mx-auto px-8 py-12">
        {/* Cover Page */}
        <div className="mb-16 text-center border-b-2 border-border pb-12">
          <h1 className="text-7xl font-serif font-bold mb-4">Mystic Forest</h1>
          <p className="text-3xl font-serif text-muted-foreground mb-8">Brand Kit</p>
          <p className="text-xl text-foreground/70">Last Updated: January 2025</p>
        </div>

        {/* Brand Essence */}
        <section className="mb-12 page-break">
          <h2 className="text-4xl font-serif font-bold mb-6 border-b border-border pb-2">Brand Essence</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-2xl font-semibold mb-2">Tagline</h3>
              <p className="text-xl italic text-foreground/80">"Offline is the New Luxury"</p>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-2">Mission</h3>
              <p className="text-lg text-foreground/80">Creating curated offline experiences that help people slow down from the everyday rush, recharge their body and mind, and reconnect with what matters most.</p>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-2">Positioning</h3>
              <p className="text-lg text-foreground/80">An antidote to digital overload through analogue retreats, events, and workshops.</p>
            </div>
          </div>
        </section>

        {/* Typography */}
        <section className="mb-12 page-break">
          <h2 className="text-4xl font-serif font-bold mb-6 border-b border-border pb-2">Typography System</h2>
          
          <div className="mb-8">
            <h3 className="text-2xl font-semibold mb-4">Font Families</h3>
            <div className="space-y-6">
              <div className="border border-border rounded-lg p-6">
                <h4 className="text-xl font-serif font-bold mb-2">Playfair Display</h4>
                <p className="text-sm text-muted-foreground mb-3">Primary Serif - Headers & Titles</p>
                <div className="space-y-2">
                  <p className="font-serif font-normal text-2xl">Regular 400</p>
                  <p className="font-serif font-medium text-2xl">Medium 500</p>
                  <p className="font-serif font-semibold text-2xl">Semi-Bold 600</p>
                  <p className="font-serif font-bold text-2xl">Bold 700</p>
                </div>
              </div>

              <div className="border border-border rounded-lg p-6">
                <h4 className="text-xl font-sans font-bold mb-2">Inter</h4>
                <p className="text-sm text-muted-foreground mb-3">Primary Sans-Serif - Body & UI</p>
                <div className="space-y-2">
                  <p className="font-sans font-light text-lg">Light 300</p>
                  <p className="font-sans font-normal text-lg">Regular 400</p>
                  <p className="font-sans font-medium text-lg">Medium 500</p>
                  <p className="font-sans font-semibold text-lg">Semi-Bold 600</p>
                  <p className="font-sans font-bold text-lg">Bold 700</p>
                </div>
              </div>

              <div className="border border-border rounded-lg p-6">
                <h4 className="text-xl font-sans font-bold mb-2">Caveat</h4>
                <p className="text-sm text-muted-foreground mb-3">Accent Handwriting - Decorative</p>
                <div className="space-y-2">
                  <p className="font-handwriting text-2xl">Regular 400 - Handwritten Notes</p>
                  <p className="font-handwriting text-2xl font-semibold">Semi-Bold 600</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-semibold mb-4">Typography Hierarchy</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-accent pl-4">
                <h1 className="font-serif font-bold">H1 - Main Heading (48px)</h1>
                <p className="text-sm text-muted-foreground">Playfair Display Bold, line-height 1.1</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h2 className="font-serif font-semibold">H2 - Section Heading (36px)</h2>
                <p className="text-sm text-muted-foreground">Playfair Display Semi-Bold, line-height 1.2</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h3 className="font-serif font-medium">H3 - Subsection (30px)</h3>
                <p className="text-sm text-muted-foreground">Playfair Display Medium, line-height 1.3</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h4 className="font-sans font-semibold">H4 - Card Heading (24px)</h4>
                <p className="text-sm text-muted-foreground">Inter Semi-Bold, line-height 1.4</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <p className="font-sans text-base">Body Text - Inter Regular (16px), line-height 1.7</p>
              </div>
            </div>
          </div>
        </section>

        {/* Color Palette */}
        <section className="mb-12 page-break">
          <h2 className="text-4xl font-serif font-bold mb-6 border-b border-border pb-2">Color Palette</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4">Core Colors</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-border rounded-lg p-4">
                  <div className="w-full h-24 bg-background border border-border rounded mb-2"></div>
                  <p className="font-semibold">Background</p>
                  <p className="text-sm text-muted-foreground">HSL: 0 0% 100%</p>
                  <p className="text-xs text-muted-foreground">Pure white</p>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <div className="w-full h-24 bg-foreground rounded mb-2"></div>
                  <p className="font-semibold">Foreground</p>
                  <p className="text-sm text-muted-foreground">HSL: 0 0% 10%</p>
                  <p className="text-xs text-muted-foreground">Primary text</p>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <div className="w-full h-24 bg-primary rounded mb-2"></div>
                  <p className="font-semibold text-primary-foreground">Primary</p>
                  <p className="text-sm text-muted-foreground">HSL: 0 0% 15%</p>
                  <p className="text-xs text-muted-foreground">Dark charcoal</p>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <div className="w-full h-24 bg-accent rounded mb-2"></div>
                  <p className="font-semibold text-accent-foreground">Accent</p>
                  <p className="text-sm text-muted-foreground">HSL: 120 45% 28%</p>
                  <p className="text-xs text-muted-foreground">Forest green</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4">Accent Highlight</h3>
              <div className="border border-border rounded-lg p-4 mb-8">
                <div className="w-full h-24 bg-lime-400 rounded mb-2"></div>
                <p className="font-semibold">Lime Highlight</p>
                <p className="text-sm text-muted-foreground">Tailwind: lime-400</p>
                <p className="text-xs text-muted-foreground mb-3">Used at 60% opacity with -1deg rotation</p>
                <div className="mt-4 p-4 bg-card border border-border rounded">
                  <p className="text-lg">
                    Example: <span className="relative inline-block">
                      <span className="absolute inset-0 bg-lime-400 opacity-60 transform -rotate-1 rounded-sm -z-10"></span>
                      <span className="relative px-2">Offline</span>
                    </span> is the new luxury
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">Usage: Special emphasis, key phrases, hero statements</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4">Nature Palette</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-border rounded-lg p-4">
                  <div className="w-full h-24 bg-nature-moss rounded mb-2"></div>
                  <p className="font-semibold text-white">Nature Moss</p>
                  <p className="text-sm text-muted-foreground">HSL: 120 20% 25%</p>
                  <p className="text-xs text-muted-foreground">Subtle green</p>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <div className="w-full h-24 bg-nature-earth rounded mb-2"></div>
                  <p className="font-semibold text-white">Nature Earth</p>
                  <p className="text-sm text-muted-foreground">HSL: 30 15% 35%</p>
                  <p className="text-xs text-muted-foreground">Warm earth tone</p>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <div className="w-full h-24 bg-nature-gold rounded mb-2"></div>
                  <p className="font-semibold text-white">Nature Gold</p>
                  <p className="text-sm text-muted-foreground">HSL: 45 35% 55%</p>
                  <p className="text-xs text-muted-foreground">Muted gold</p>
                </div>
                <div className="border border-border rounded-lg p-4">
                  <div className="w-full h-24 bg-nature-stone rounded mb-2"></div>
                  <p className="font-semibold text-white">Nature Stone</p>
                  <p className="text-sm text-muted-foreground">HSL: 0 0% 55%</p>
                  <p className="text-xs text-muted-foreground">Grey stone</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Voice */}
        <section className="mb-12 page-break">
          <h2 className="text-4xl font-serif font-bold mb-6 border-b border-border pb-2">Brand Voice & Tone</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-semibold mb-3">Core Values</h3>
              <ul className="space-y-2 text-lg">
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>Authentic</strong> - No esoteric clichés, grounded in reality</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>Mindful</strong> - Intentional, thoughtful, present</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>Warm</strong> - Inviting, human, caring</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>Sophisticated</strong> - Elegant without being pretentious</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent">✓</span>
                  <span><strong>Rebellious</strong> - Quietly defiant against hustle culture</span>
                </li>
              </ul>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-accent/20 rounded-lg p-6 bg-accent/5">
                <h3 className="text-xl font-semibold mb-3 text-accent">Do's ✓</h3>
                <ul className="space-y-1 text-sm">
                  <li>• Use simple, clear language</li>
                  <li>• Speak directly to the reader</li>
                  <li>• Emphasize slowness, presence</li>
                  <li>• Be honest about challenges</li>
                  <li>• Highlight luxury of less</li>
                </ul>
              </div>
              <div className="border border-destructive/20 rounded-lg p-6 bg-destructive/5">
                <h3 className="text-xl font-semibold mb-3 text-destructive">Don'ts ✗</h3>
                <ul className="space-y-1 text-sm">
                  <li>• Avoid "woo-woo" language</li>
                  <li>• No buzzwords or corporate speak</li>
                  <li>• No aggressive anti-tech rhetoric</li>
                  <li>• Don't promise instant fixes</li>
                  <li>• Avoid clichéd wellness phrases</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-3">Example Headlines</h3>
              <div className="space-y-2">
                <p className="text-xl font-serif italic">"Offline is the New Luxury"</p>
                <p className="text-xl font-serif italic">"Two Rhythms. One Intention."</p>
                <p className="text-xl font-serif italic">"Step Deeper into the Forest"</p>
              </div>
            </div>
          </div>
        </section>

        {/* Spacing System */}
        <section className="mb-12 page-break">
          <h2 className="text-4xl font-serif font-bold mb-6 border-b border-border pb-2">Spacing System</h2>
          <div className="space-y-4">
            {[
              { name: 'xs', value: '8px', usage: 'Tight spacing, icon padding' },
              { name: 'sm', value: '12px', usage: 'Small gaps between items' },
              { name: 'md', value: '16px', usage: 'Default spacing' },
              { name: 'lg', value: '24px', usage: 'Medium sections' },
              { name: 'xl', value: '32px', usage: 'Large sections' },
              { name: '2xl', value: '48px', usage: 'Section spacing' },
              { name: '3xl', value: '64px', usage: 'Major section spacing' },
              { name: '4xl', value: '96px', usage: 'Hero section spacing' },
            ].map((space) => (
              <div key={space.name} className="flex items-center gap-4 border-b border-border pb-2">
                <div className="w-32 font-mono text-sm">{space.name}</div>
                <div className="w-20 font-mono text-sm text-muted-foreground">{space.value}</div>
                <div className="flex-1 text-sm text-foreground/70">{space.usage}</div>
                <div className="w-24 h-8 bg-accent rounded" style={{ width: space.value }}></div>
              </div>
            ))}
          </div>
        </section>

        {/* Logo & Assets */}
        <section className="mb-12 page-break">
          <h2 className="text-4xl font-serif font-bold mb-6 border-b border-border pb-2">Logo & Visual Identity</h2>
          <div className="space-y-6">
            <div className="border border-border rounded-lg p-8 bg-card text-center">
              <p className="text-5xl font-serif font-bold mb-2">Mystic Forest</p>
              <p className="text-lg text-muted-foreground">Primary Wordmark</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Logo Usage Guidelines</h3>
              <ul className="space-y-1 text-foreground/80">
                <li>• Minimum size: 120px width</li>
                <li>• Clear space: Equal to height of logo on all sides</li>
                <li>• Always ensure sufficient contrast with background</li>
                <li>• Available in light and dark versions</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Accessibility */}
        <section className="mb-12">
          <h2 className="text-4xl font-serif font-bold mb-6 border-b border-border pb-2">Accessibility Standards</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">WCAG Compliance</h3>
              <p className="text-foreground/80 mb-2">Level: AA (minimum)</p>
              <ul className="space-y-1 text-foreground/80">
                <li>• Normal text contrast: 4.5:1</li>
                <li>• Large text contrast: 3:1</li>
                <li>• UI components contrast: 3:1</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Best Practices</h3>
              <ul className="space-y-1 text-foreground/80">
                <li>• Semantic HTML structure</li>
                <li>• Alt text for all images</li>
                <li>• Keyboard navigation support</li>
                <li>• Visible focus indicators</li>
                <li>• ARIA labels where needed</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t-2 border-border text-center text-muted-foreground">
          <p className="text-sm">Mystic Forest Brand Kit v1.0 • January 2025</p>
          <p className="text-xs mt-2">For questions or updates, contact the Mystic Forest team</p>
        </footer>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .page-break {
            page-break-after: always;
            break-after: page;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}